// Catches render errors so a single page crash doesn't blank the entire app.

import { Component, type ErrorInfo, type ReactNode, type JSX } from "react";

interface Props { children: ReactNode; }
interface State { error: Error | null; }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 max-w-md w-full text-center space-y-4">
            <p className="text-lg font-semibold text-slate-900">Something went wrong</p>
            <p className="text-sm text-slate-500">{this.state.error.message}</p>
            <button
              onClick={() => { this.setState({ error: null }); window.location.href = "/dashboard"; }}
              className="px-4 py-2 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children as JSX.Element;
  }
}
